import React from 'react';
import { I18n as I, I18n } from 'react-redux-i18n';

import { AuctionStatuses, LoaderOverlay, PartyTypes } from 'vdlib';
import LookupsHelper from 'lib/LookupsHelper';
import { usePartyFlags } from 'lib/Hooks';

import Portlet from 'components/layout/Portlet';
import FolderDataPane from './FolderDataPane';

import AuctionImages from 'containers/auctions/AuctionImagesContainer';

import { Row, Col } from 'antd';

const afterActiveStatuses = [
  AuctionStatuses.ForAcceptance,
  AuctionStatuses.ForDispatch,
  AuctionStatuses.Finished,
];

const modalTypes = {
  acceptAuction: 'accept-auction',
  auctionComments: 'auction-comments',
  auctionDuration: 'auction-duration',
  cancelAuction: 'cancel-auction',
  dispatchAuction: 'dispatch-auction',
  dispatchProblem: 'dispatch-problem',
  rejectAuction: 'reject-auction',
};

const isValidId = id => {
  try {
    return String(parseInt(id)) === String(id);
  } catch (e) {
    return false;
  }
};

const AuctionsFolder = ({
  auction,
  auctionStatuses,
  clearData,
  comments,
  fetchAuction,
  fetchImages,
  fetchingImages,
  fuelTypes,
  gearBoxes,
  images,
  params,
  partyTypes,
  submitting,
  user,
  vehicleTypes,
  wheelDrives,
}) => {
  const { isInsurance, isMerchant, isOperator, partyType } = usePartyFlags(
    user,
    partyTypes
  );

  const status = React.useMemo(
    () =>
      auction
        ? LookupsHelper.getDescription(auctionStatuses, auction.StatusID)
        : null,
    [auction, auctionStatuses]
  );

  const init = React.useCallback(() => {
    clearData();
    fetchAuction(params.id);
  }, [params.id]); //eslint-disable-line

  // init effect
  React.useEffect(() => {
    if (isValidId(params.id)) init();
  }, [params.id, submitting]); //eslint-disable-line

  React.useEffect(() => {
    if (!user || !auction) return;
    const { PartyId } = user;

    if (auction.FolderID) fetchImages(auction.FolderID);
  }, [auction]); //eslint-disable-line

  // cleanup effect
  React.useEffect(() => {
    return () => {
      clearData();
    };
  }, []); //eslint-disable-line

  if (!isValidId(params.id)) return false;

  const portletTitle = `${I.t('FOLDER_NO_C')} - ${auction?.FolderNumber}`;

  return (
    <div className="auction-wrap">
      <LoaderOverlay loading={!auction} />
      {auction && (
        <div>
          <Portlet
            key="main"
            icon="icon-settings"
            title={portletTitle}
            backButtonUrl={''}
            showBackButton={true}
          >
            <Row>
              <Col xs={24} xl={14}>
                <AuctionImages
                  auction={auction}
                  fetchingImages={fetchingImages}
                  images={images}
                  onImagesChanged={fetchImages}
                  className={null}
                />
              </Col>
              <Col xs={24} xl={10}>
                <FolderDataPane
                  auction={auction}
                  comments={comments}
                  vehicleType={LookupsHelper.getDescription(
                    vehicleTypes,
                    auction.VehicleTypeID
                  )}
                  fuelType={LookupsHelper.getDescription(
                    fuelTypes,
                    auction.FuelTypeID
                  )}
                  gearBox={LookupsHelper.getDescription(
                    gearBoxes,
                    auction.GearboxID
                  )}
                  wheelDrive={LookupsHelper.getDescription(
                    wheelDrives,
                    auction.WheeldriveID
                  )}
                  showInsurance={
                    partyType === PartyTypes.Operator ||
                    partyType === PartyTypes.Admin_Insurance ||
                    partyType === PartyTypes.Insurance
                  }
                  showOwnerInfo={
                    partyType === PartyTypes.Operator ||
                    partyType === PartyTypes.Insurance ||
                    partyType === PartyTypes.Admin_Insurance ||
                    status === AuctionStatuses.Finished
                  }
                  viewOnly={true}
                  status={LookupsHelper.getDescription(
                    auctionStatuses,
                    auction.StatusID
                  )}
                  partyType={LookupsHelper.getDescription(
                    partyTypes,
                    user.PartyType
                  )}
                />
              </Col>
            </Row>
            <div className="row" />
          </Portlet>
        </div>
      )}
    </div>
  );
};

export default AuctionsFolder;
